import { useRef, useLayoutEffect, useState, useMemo } from "react";
import { Box, Button, Typography, keyframes } from "@mui/material";
import JigsawDieLineOverlay from "./JigsawDieLineOverlay";
import { getBoardMatSurfaceLayers } from "./boardMatSurface";
import { PIECE_GLUE_GLOW_FILTER } from "./jigsawPieceGlueEffects";
import { BOARD_SIZE, PieceVisual } from "./PieceVisual";
import SlidingLineCompleteFx from "../sliding/SlidingLineCompleteFx";

const CONTAINER_CQ_SUPPORTED =
    typeof CSS !== "undefined" && CSS.supports("width", "1cqw");

const placementHintPulse = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 2px rgba(59, 166, 102, 0.35));
    opacity: 0.38;
  }
  50% {
    filter: drop-shadow(0 0 8px rgba(59, 166, 102, 0.85));
    opacity: 0.62;
  }
`;

const placementHintOutlinePulse = keyframes`
  0%, 100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
`;

function measureBoardContainer(el, fitParent) {
    const w = el.clientWidth;
    const h = el.clientHeight;
    const side = fitParent && w > 0 && h > 0 ? Math.min(w, h) : w;
    const scale = side > 0 ? side / BOARD_SIZE : 1;
    const displaySize =
        fitParent && side > 0 ? { width: side, height: side } : { width: w, height: h };
    return { scale, displaySize };
}

const Z_BOARD_PLACED = 1;
const Z_BOARD_MISPLACED = 100;

function isPieceAtHome(b, gridN) {
    const cellPxN = BOARD_SIZE / gridN;
    const homeX = (b.pieceIndex % gridN) * cellPxN;
    const homeY = Math.floor(b.pieceIndex / gridN) * cellPxN;
    const tol = cellPxN * 0.22;
    return Math.abs(b.x - homeX) <= tol && Math.abs(b.y - homeY) <= tol;
}

/**
 * Play board: free-positioned pieces + die overlay. Ref used for drop hit-testing.
 * Uses CSS transform scale to fill container width while keeping internal coords at BOARD_SIZE.
 * getBoundingClientRect() on boardRef accounts for the transform, so parent drag logic
 * must divide (mousePos - rect.left) by scale to get internal coordinates.
 */
export default function CanvasBoard({
    boardRef,
    /** Optional: viewport element used for drop hit-testing when the board is cropped. */
    boardViewportRef = null,
    boardPieces,
    grid,
    imageUrl,
    paths,
    svgPathFallback,
    dragging,
    showDragOutline,
    headerTitle = "Board",
    headerExtra = null,
    placedCount,
    total,
    isSolved,
    onBoardPieceMouseDown,
    showPieceGridCoords = false,
    onScaleChange,
    onDisplaySizeChange,
    fitParent = false,
    /** Max width/height of the square canvas (not including the header row). */
    squareMaxSize,
    /**
     * OBS-safe: paint at this exact CSS side (px). Skips container-query scale —
     * CEF in OBS Browser Source often mis-evaluates cqw/cqmin.
     */
    explicitDisplaySidePx,
    showHeader = true,
    /** pieceIndex → selection order for overlapping misplaced pieces */
    pieceFocusOrder = {},
    /** Puzzle mat fill color (hex) — used when matPreset is omitted */
    matColor = "#3A3A3A",
    /** Full mat preset (hex + surface texture profile) */
    matPreset = null,
    /** Engraved groove styling tuned to mat brightness */
    grooveStyle = null,
    /** Active correct-placement shine overlays: { pieceIndex, id }[] */
    activePieceShines = [],
    /** boardId → settle animation key for wrong-place pop & settle */
    pieceSettleKeys = null,
    /** boardId → flash key played once when pieces glue together away from home */
    pieceGlueKeys = null,
    /** When true, pieces are not draggable (admin preview). */
    readOnly = false,
    /** When true, pieces already snapped to their exact home cell cannot be dragged. */
    lockHomePieces = false,
    /** Row/column complete FX: { id, rows, cols, praise } */
    lineMatchFx = null,
    onLineMatchFxComplete = null,
    /** Piece indices currently shown as placement-hint ghosts (home cells). */
    placementHintPieceIndices = null,
    /**
     * Basket region guide (same idea as color-basket mini-map): tint home cells + dashed bounds.
     * `{ pieceIndexes: number[], colorHex: string }`
     */
    regionGuide = null,
    /**
     * When true with a region guide, crop the board viewport to the guide bounds
     * (viewport aspect ratio matches the placement region — no empty mat letterboxing).
     */
    cropToRegionGuide = false,
    /**
     * Expand the crop viewport by this many cells on each side so pieces can sit
     * outside the home region while still framing the basket.
     */
    cropPaddingCells = 0,
    /**
     * Max CSS height (px) for a cropped region board.
     * With fitParent, also caps the height of the region-fitted viewport.
     */
    cropMaxHeight = 360,
    /** Engraved piece cut lines on the mat. */
    showDieLines = true,
}) {
    const containerRef = useRef(null);
    const cropHostRef = useRef(null);
    const [fallbackScale, setFallbackScale] = useState(1);
    /** Cropped viewport size (px) when cropToRegionGuide is on — matches region aspect ratio. */
    const [cropViewSize, setCropViewSize] = useState({ w: 0, h: 0 });
    /** Scale that fits the region inside the viewport (contain). */
    const [cropFitScale, setCropFitScale] = useState(0);

    /** Group ids with more than one member — a lone group id is not glued to anything. */
    const gluedGroupIds = useMemo(() => {
        const counts = new Map();
        for (const b of boardPieces) {
            if (!b?.groupId) continue;
            counts.set(b.groupId, (counts.get(b.groupId) ?? 0) + 1);
        }
        const glued = new Set();
        for (const [groupId, count] of counts) {
            if (count > 1) glued.add(groupId);
        }
        return glued;
    }, [boardPieces]);

    const matSurface = useMemo(
        () => getBoardMatSurfaceLayers(matPreset ?? { hex: matColor, surface: "matte" }),
        [matPreset, matColor],
    );

    const regionGuideLayout = useMemo(() => {
        const indexes = Array.isArray(regionGuide?.pieceIndexes) ? regionGuide.pieceIndexes : [];
        const colorHex = regionGuide?.colorHex || "#3BA666";
        if (!grid || grid <= 0 || indexes.length === 0) return null;
        const cellPxN = BOARD_SIZE / grid;
        let minCol = Infinity;
        let minRow = Infinity;
        let maxCol = -Infinity;
        let maxRow = -Infinity;
        const cells = [];
        for (const raw of indexes) {
            const pieceIndex = Number(raw);
            if (!Number.isInteger(pieceIndex) || pieceIndex < 0 || pieceIndex >= grid * grid) continue;
            const col = pieceIndex % grid;
            const row = Math.floor(pieceIndex / grid);
            minCol = Math.min(minCol, col);
            minRow = Math.min(minRow, row);
            maxCol = Math.max(maxCol, col);
            maxRow = Math.max(maxRow, row);
            cells.push({
                pieceIndex,
                left: col * cellPxN,
                top: row * cellPxN,
                size: cellPxN,
            });
        }
        if (cells.length === 0) return null;
        return {
            colorHex,
            cells,
            bounds: {
                left: minCol * cellPxN,
                top: minRow * cellPxN,
                width: (maxCol - minCol + 1) * cellPxN,
                height: (maxRow - minRow + 1) * cellPxN,
            },
            colSpan: maxCol - minCol + 1,
            rowSpan: maxRow - minRow + 1,
        };
    }, [grid, regionGuide]);

    const cropActive =
        cropToRegionGuide &&
        regionGuideLayout &&
        regionGuideLayout.bounds.width > 0 &&
        regionGuideLayout.bounds.height > 0;
    const cropBounds = useMemo(() => {
        if (!cropActive || !regionGuideLayout) return null;
        const b = regionGuideLayout.bounds;
        const padCells = Number(cropPaddingCells);
        if (!(grid > 0) || !(padCells > 0)) return b;
        const pad = (BOARD_SIZE / grid) * padCells;
        const left = Math.max(0, b.left - pad);
        const top = Math.max(0, b.top - pad);
        const right = Math.min(BOARD_SIZE, b.left + b.width + pad);
        const bottom = Math.min(BOARD_SIZE, b.top + b.height + pad);
        return {
            left,
            top,
            width: Math.max(b.width, right - left),
            height: Math.max(b.height, bottom - top),
        };
    }, [cropActive, regionGuideLayout, cropPaddingCells, grid]);

    const lastReportedSidePxRef = useRef(0);
    const explicitSide =
        Number.isFinite(explicitDisplaySidePx) && explicitDisplaySidePx >= 20
            ? Math.round(explicitDisplaySidePx)
            : 0;
    const explicitScale = explicitSide > 0 ? explicitSide / BOARD_SIZE : 0;

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const apply = () => {
            const measure = () => {
                if (cropActive && cropBounds) {
                    const host = cropHostRef.current;
                    const hostW = host?.clientWidth || el.parentElement?.clientWidth || el.clientWidth || 0;
                    const hostH = host?.clientHeight || el.parentElement?.clientHeight || 0;
                    const maxHProp = Number(cropMaxHeight);
                    const aspect = cropBounds.width / cropBounds.height;
                    // Size the viewport to the region aspect ratio (no empty letterbox mat).
                    let viewW;
                    let viewH;
                    if (fitParent && hostW > 0 && hostH > 0) {
                        if (hostW / hostH > aspect) {
                            viewH = Math.max(40, hostH);
                            viewW = viewH * aspect;
                        } else {
                            viewW = Math.max(40, hostW);
                            viewH = viewW / aspect;
                        }
                        if (maxHProp > 0 && viewH > maxHProp) {
                            viewH = maxHProp;
                            viewW = viewH * aspect;
                        }
                    } else {
                        // Prefer full available width; only shrink if an explicit height cap is set.
                        viewW = Math.max(40, hostW || 360);
                        viewH = viewW / aspect;
                        if (maxHProp > 0 && viewH > maxHProp) {
                            viewH = maxHProp;
                            viewW = viewH * aspect;
                        }
                    }
                    viewW = Math.round(viewW);
                    viewH = Math.round(viewH);
                    if (
                        Math.abs(el.clientWidth - viewW) > 0.5
                        || Math.abs(el.clientHeight - viewH) > 0.5
                    ) {
                        el.style.width = `${viewW}px`;
                        el.style.height = `${viewH}px`;
                    }
                    const fitScale = Math.min(
                        viewW / cropBounds.width,
                        viewH / cropBounds.height,
                    );
                    setCropViewSize((prev) =>
                        prev.w === viewW && prev.h === viewH ? prev : { w: viewW, h: viewH },
                    );
                    setCropFitScale((prev) => (Math.abs(prev - fitScale) < 1e-6 ? prev : fitScale));
                    const displaySize = { width: viewW, height: viewH };
                    const reportKey = viewW * 10000 + viewH;
                    if (viewW >= 20 && viewH >= 20 && reportKey !== lastReportedSidePxRef.current) {
                        lastReportedSidePxRef.current = reportKey;
                        if (onScaleChange) onScaleChange(fitScale);
                        if (onDisplaySizeChange) onDisplaySizeChange(displaySize);
                    }
                    return;
                }

                el.style.width = "";
                el.style.height = "";
                setCropViewSize({ w: 0, h: 0 });
                setCropFitScale(0);

                let sidePx = 0;
                let displaySize = { width: 0, height: 0 };

                if (explicitSide > 0) {
                    sidePx = explicitSide;
                    displaySize = { width: sidePx, height: sidePx };
                } else {
                    const boardEl = boardRef?.current;
                    if (boardEl) {
                        const rect = boardEl.getBoundingClientRect();
                        if (rect.width > 0 && rect.height > 0) {
                            sidePx = Math.round(Math.min(rect.width, rect.height));
                            displaySize = { width: sidePx, height: sidePx };
                        }
                    }

                    if (sidePx < 20) {
                        const measured = measureBoardContainer(el, fitParent);
                        sidePx = Math.round(
                            Math.min(
                                measured.displaySize.width > 0 ? measured.displaySize.width : 0,
                                measured.displaySize.height > 0
                                    ? measured.displaySize.height
                                    : measured.displaySize.width,
                            ) || measured.scale * BOARD_SIZE,
                        );
                        displaySize =
                            measured.displaySize.width > 0
                                ? measured.displaySize
                                : { width: sidePx, height: sidePx };
                    }
                }

                const scale = sidePx > 0 ? sidePx / BOARD_SIZE : 1;

                if (!CONTAINER_CQ_SUPPORTED || explicitSide > 0) {
                    setFallbackScale((prev) => (prev === scale ? prev : scale));
                }

                if (sidePx >= 20 && sidePx !== lastReportedSidePxRef.current) {
                    lastReportedSidePxRef.current = sidePx;
                    if (onScaleChange) onScaleChange(scale);
                    if (onDisplaySizeChange) onDisplaySizeChange(displaySize);
                }
            };

            requestAnimationFrame(measure);
        };

        apply();
        if (explicitSide > 0 && !cropActive) return undefined;

        const observer = new ResizeObserver(apply);
        observer.observe(el);
        if (cropHostRef.current) observer.observe(cropHostRef.current);
        const boardEl = boardRef?.current;
        if (boardEl) observer.observe(boardEl);
        return () => observer.disconnect();
    }, [
        fitParent,
        squareMaxSize,
        explicitSide,
        onScaleChange,
        onDisplaySizeChange,
        boardRef,
        cropActive,
        cropBounds,
        cropMaxHeight,
    ]);

    const cropPadX =
        cropActive && cropBounds && cropViewSize.w > 0 && cropFitScale > 0
            ? (cropViewSize.w - cropBounds.width * cropFitScale) / 2
            : 0;
    const cropPadY =
        cropActive && cropBounds && cropViewSize.h > 0 && cropFitScale > 0
            ? (cropViewSize.h - cropBounds.height * cropFitScale) / 2
            : 0;

    const boardTransform = cropActive && cropBounds
        ? cropFitScale > 0
            ? `translate(${cropPadX - cropBounds.left * cropFitScale}px, ${cropPadY - cropBounds.top * cropFitScale}px) scale(${cropFitScale})`
            : "none"
        : explicitScale > 0
          ? `scale(${explicitScale})`
          : CONTAINER_CQ_SUPPORTED
            ? fitParent
                ? `translate(-50%, -50%) scale(calc(100cqmin / ${BOARD_SIZE}px))`
                : `scale(calc(100cqw / ${BOARD_SIZE}px))`
            : fitParent
              ? `translate(-50%, -50%) scale(${fallbackScale})`
              : `scale(${fallbackScale})`;

    return (
        <Box
            ref={cropActive ? cropHostRef : undefined}
            sx={{
                width: "100%",
                ...(cropActive
                    ? {
                          display: "flex",
                          justifyContent: fitParent ? "center" : "flex-start",
                          alignItems: fitParent ? "center" : "flex-start",
                          width: "100%",
                      }
                    : {}),
                ...(fitParent || explicitSide > 0
                    ? {
                          height: "100%",
                          minHeight: 0,
                          flex: 1,
                          display: "flex",
                          ...(cropActive
                              ? {}
                              : { flexDirection: "column" }),
                      }
                    : {}),
            }}
        >
            {showHeader ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1, md: 2 },
                        mb: 1,
                        flexShrink: 0,
                        flexWrap: "wrap",
                        width: "100%",
                        minWidth: 0,
                        rowGap: 0.5,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 auto", minWidth: 0 }}>
                        <Typography variant="subtitle2" component="span">
                            {headerTitle} ({placedCount}/{total})
                        </Typography>
                        {placedCount === total && isSolved && (
                            <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700 }}>
                                Solved!
                            </Typography>
                        )}
                    </Box>
                    {headerExtra ? (
                        <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center", ml: "auto" }}>
                            {headerExtra}
                        </Box>
                    ) : null}
                </Box>
            ) : null}
            <Box
                ref={(node) => {
                    containerRef.current = node;
                    if (boardViewportRef) {
                        if (typeof boardViewportRef === "function") {
                            boardViewportRef(node);
                        } else {
                            boardViewportRef.current = node;
                        }
                    }
                }}
                sx={{
                    width: cropActive ? undefined : "100%",
                    maxWidth: "100%",
                    position: "relative",
                    overflow: cropActive ? "hidden" : "visible",
                    borderRadius: cropActive ? 1 : undefined,
                    flexShrink: 0,
                    ...(explicitSide > 0 && !cropActive
                        ? {
                              width: explicitSide,
                              height: explicitSide,
                              maxWidth: explicitSide,
                              maxHeight: explicitSide,
                              flexShrink: 0,
                          }
                        : {
                              containerType: "size",
                              ...(fitParent && !cropActive
                                  ? { flex: 1, minHeight: 0 }
                                  : !cropActive
                                    ? {
                                          aspectRatio: "1 / 1",
                                          ...(squareMaxSize
                                              ? { maxWidth: squareMaxSize, maxHeight: squareMaxSize }
                                              : {}),
                                      }
                                    : {
                                          // Size is set in the layout effect to match region aspect ratio.
                                          maxWidth: "100%",
                                          width: "100%",
                                          ...(fitParent
                                              ? {}
                                              : Number(cropMaxHeight) > 0
                                                ? { maxHeight: cropMaxHeight }
                                                : {}),
                                      }),
                          }),
                }}
            >
                <Box
                    ref={boardRef}
                    sx={{
                        position: "absolute",
                        top: fitParent && explicitSide <= 0 && !cropActive ? "50%" : 0,
                        left: fitParent && explicitSide <= 0 && !cropActive ? "50%" : 0,
                        width: BOARD_SIZE,
                        height: BOARD_SIZE,
                        transform: boardTransform,
                        transformOrigin:
                            cropActive
                                ? "top left"
                                : fitParent && explicitSide <= 0
                                  ? "center center"
                                  : "top left",
                        borderRadius: cropActive ? 0 : 1,
                        overflow: "visible",
                        outline: showDragOutline ? "2px dashed #3BA666" : "none",
                        outlineOffset: 0,
                        isolation: "isolate",
                        ...matSurface.baseSx,
                    }}
                >
                    <Box aria-hidden sx={matSurface.textureSx} />
                    {showDieLines ? (
                        <JigsawDieLineOverlay
                            pieces={paths}
                            opacity={0.72}
                            svgPathFallback={svgPathFallback}
                            engravedGroove
                            strokeWidth={0.35}
                            grooveShadowOpacity={grooveStyle?.shadowOpacity}
                            grooveHighlightOpacity={grooveStyle?.highlightOpacity}
                            grooveStroke={grooveStyle?.stroke}
                        />
                    ) : null}
                    {regionGuideLayout
                        ? regionGuideLayout.cells.map((cell) => (
                              <Box
                                  key={`region-guide-cell-${cell.pieceIndex}`}
                                  aria-hidden
                                  sx={{
                                      position: "absolute",
                                      left: cell.left,
                                      top: cell.top,
                                      width: cell.size,
                                      height: cell.size,
                                      bgcolor: regionGuideLayout.colorHex,
                                      opacity: 0.38,
                                      outline: "1px solid rgba(255,255,255,0.35)",
                                      outlineOffset: -1,
                                      pointerEvents: "none",
                                      zIndex: 0,
                                      boxSizing: "border-box",
                                  }}
                              />
                          ))
                        : null}
                    {regionGuideLayout ? (
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                left: regionGuideLayout.bounds.left,
                                top: regionGuideLayout.bounds.top,
                                width: regionGuideLayout.bounds.width,
                                height: regionGuideLayout.bounds.height,
                                border: "2px dashed rgba(255,255,255,0.92)",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
                                pointerEvents: "none",
                                // Behind pieces so the guide never cuts across them.
                                zIndex: 0,
                                boxSizing: "border-box",
                            }}
                        />
                    ) : null}
                    {Array.isArray(placementHintPieceIndices) && placementHintPieceIndices.length > 0
                        ? placementHintPieceIndices.map((pieceIndex) => {
                              const cellPxN = BOARD_SIZE / grid;
                              const homeX = (pieceIndex % grid) * cellPxN;
                              const homeY = Math.floor(pieceIndex / grid) * cellPxN;
                              return (
                                  <Box
                                      key={`placement-hint-outline-${pieceIndex}`}
                                      aria-hidden
                                      sx={{
                                          position: "absolute",
                                          left: homeX,
                                          top: homeY,
                                          width: cellPxN,
                                          height: cellPxN,
                                          boxSizing: "border-box",
                                          borderRadius: 0.5,
                                          border: "2px dashed rgba(59, 166, 102, 0.9)",
                                          backgroundColor: "transparent",
                                          pointerEvents: "none",
                                          zIndex: Z_BOARD_MISPLACED + 40,
                                          animation: `${placementHintOutlinePulse} 1.1s ease-in-out infinite`,
                                      }}
                                  />
                              );
                          })
                        : null}
                    {Array.isArray(placementHintPieceIndices) && placementHintPieceIndices.length > 0
                        ? placementHintPieceIndices.map((pieceIndex) => {
                              const cellPxN = BOARD_SIZE / grid;
                              const homeX = (pieceIndex % grid) * cellPxN;
                              const homeY = Math.floor(pieceIndex / grid) * cellPxN;
                              return (
                                  <Box
                                      key={`placement-hint-piece-${pieceIndex}`}
                                      aria-hidden
                                      sx={{
                                          position: "absolute",
                                          left: homeX,
                                          top: homeY,
                                          pointerEvents: "none",
                                          zIndex: Z_BOARD_MISPLACED + 50,
                                          animation: `${placementHintPulse} 1.1s ease-in-out infinite`,
                                      }}
                                  >
                                      <PieceVisual
                                          pieceIndex={pieceIndex}
                                          imageUrl={imageUrl}
                                          grid={grid}
                                          paths={paths}
                                          bevel3d={false}
                                          showGridCoords={false}
                                      />
                                  </Box>
                              );
                          })
                        : null}
                    {boardPieces
                        .map((b, idx) => ({ b, idx }))
                        .sort((a, c) => {
                            const aHome = isPieceAtHome(a.b, grid);
                            const cHome = isPieceAtHome(c.b, grid);
                            if (aHome !== cHome) return aHome ? -1 : 1;
                            if (!aHome) {
                                const aFocus = pieceFocusOrder[a.b.pieceIndex] ?? a.idx;
                                const cFocus = pieceFocusOrder[c.b.pieceIndex] ?? c.idx;
                                return aFocus - cFocus;
                            }
                            return a.idx - c.idx;
                        })
                        .map(({ b, idx }) => {
                            const atHome = isPieceAtHome(b, grid);
                            const focusRank = pieceFocusOrder[b.pieceIndex] ?? idx;
                            const settleKey = pieceSettleKeys?.[b.id];
                            const glueKey = pieceGlueKeys?.[b.id];
                            // Home pieces are already read as correct; only loose clusters need it.
                            const glued =
                                !atHome && !!b.groupId && gluedGroupIds.has(b.groupId);
                            const isDraggingPiece =
                                (dragging?.groupId && b.groupId && b.groupId === dragging.groupId)
                                || dragging?.boardId === b.id;
                            const pieceLocked = readOnly || (lockHomePieces && atHome);
                            const startPieceDrag = pieceLocked
                                ? undefined
                                : (e) =>
                                      onBoardPieceMouseDown(e, b.pieceIndex, {
                                          boardId: b.id,
                                          boardX: b.x,
                                          boardY: b.y,
                                          groupId: b.groupId,
                                      });
                            const pointerOk =
                                typeof window !== "undefined" && typeof window.PointerEvent !== "undefined";
                            return (
                                <div
                                    key={b.id}
                                    data-jigsaw-board-piece=""
                                    onPointerDown={pointerOk ? startPieceDrag : undefined}
                                    onMouseDown={pointerOk ? undefined : startPieceDrag}
                                    onTouchStart={pointerOk ? undefined : startPieceDrag}
                                    style={{
                                        position: "absolute",
                                        left: b.x,
                                        top: b.y,
                                        cursor: pieceLocked ? "default" : "grab",
                                        // Keep in DOM while dragging (opacity only) so pointerup isn't cancelled
                                        // when a glued group would otherwise unmount mid-gesture.
                                        pointerEvents:
                                            pieceLocked || isDraggingPiece ? "none" : "auto",
                                        opacity: isDraggingPiece ? 0.25 : 1,
                                        // Keep home/clued pieces above region-guide chrome so they stay grabbable.
                                        zIndex: atHome
                                            ? Z_BOARD_PLACED + 20 + idx
                                            : Z_BOARD_MISPLACED + focusRank,
                                        touchAction: pieceLocked ? "auto" : "none",
                                        userSelect: "none",
                                        filter: glued ? PIECE_GLUE_GLOW_FILTER : undefined,
                                    }}
                                >
                                    <div
                                        key={`${settleKey || "idle"}-${glueKey || "idle"}`}
                                        className={
                                            [
                                                settleKey ? "jigsaw-piece-settle" : null,
                                                glueKey ? "jigsaw-piece-glue-flash" : null,
                                            ]
                                                .filter(Boolean)
                                                .join(" ") || undefined
                                        }
                                    >
                                        <PieceVisual
                                            pieceIndex={b.pieceIndex}
                                            imageUrl={imageUrl}
                                            grid={grid}
                                            paths={paths}
                                            showGridCoords={showPieceGridCoords}
                                            lockShineKey={
                                                activePieceShines.find(
                                                    (shine) => shine.boardId === b.id,
                                                )?.id ?? null
                                            }
                                        />
                                    </div>
                                </div>
                            );
                        })}
                </Box>
                {lineMatchFx ? (
                    <SlidingLineCompleteFx
                        fxId={lineMatchFx.id}
                        gridSize={grid}
                        rows={lineMatchFx.rows}
                        cols={lineMatchFx.cols}
                        praise={lineMatchFx.praise}
                        onComplete={onLineMatchFxComplete}
                    />
                ) : null}
            </Box>
        </Box>
    );
}
